import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Autocomplete,
  Collapse,
  Divider,
  useMediaQuery,
  useTheme,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Popover from "@mui/material/Popover";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import InfiniteScroll from "react-infinite-scroll-component";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  createDesignAndDrawing,
  fetchDesignAndDrawing,
  ApproveSolistapi,
  updateDesignAndDrawing,
  deleteDesignAndDrawing,
} from "../../../Services/Services";

const DesignAndDrawing = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expandedItem, setExpandedItem] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // Search Filters
  const [projectFilter, setProjectFilter] = useState("");

  const [openAddModal, setOpenAddModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentEditId, setCurrentEditId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [errors, setErrors] = useState({});
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // ========== DROPDOWN OPTIONS STATE ==========
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);

  // Loading states for dropdowns
  const [divisionLoading, setDivisionLoading] = useState(false);
  const [projectLoading, setProjectLoading] = useState(false);

  // Pagination for dropdowns
  const [divisionPage, setDivisionPage] = useState(1);
  const [projectPage, setProjectPage] = useState(1);
  const [hasMoreDivisions, setHasMoreDivisions] = useState(true);
  const [hasMoreProjects, setHasMoreProjects] = useState(true);

  // Search inputs for dropdowns
  const [divisionSearchInput, setDivisionSearchInput] = useState("");
  const [projectSearchInput, setProjectSearchInput] = useState("");

  const isInitialMount = useRef(true);

  // Get user_id from localStorage
  const getUserId = () => {
    return localStorage.getItem("user_id") || "";
  };

  // Get division_id from localStorage
  const getSelectedDivision = () => {
    try {
      const stored = localStorage.getItem("selectedDivision");
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error("Failed to parse selectedDivision:", e);
      return null;
    }
  };

  const storedDivision = getSelectedDivision();

  const getInitialFormData = () => ({
    division: storedDivision?.division_id || "",
    project: null,
    file: null,
    created_by: getUserId(),
  });

  const [formData, setFormData] = useState(getInitialFormData());

  const truncateText = (text, limit = 20) => {
    if (!text) return "";
    return text.length > limit ? text.substring(0, limit) + "..." : text;
  };

  const getFileName = (file) => {
    if (!file) return "";
    if (!file.includes("/")) return file;
    try {
      const url = new URL(file);
      const pathname = url.pathname;
      return pathname.split("/").pop() || file;
    } catch {
      return file.split("/").pop() || file;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // ========== FETCH PROJECTS FOR SELECTED DIVISION ==========
  const fetchProjectsForDivision = useCallback(
    async (divisionId, searchTerm = "", pageNum = 1, reset = true) => {
      try {
        setProjectLoading(true);
        const data = await ApproveSolistapi(pageNum, {
          division: divisionId,
          project_name: searchTerm,
        });

        const results = data.results || data.data || data || [];

        if (reset) {
          setProjectOptions(results);
        } else {
          setProjectOptions((prev) => [...prev, ...results]);
        }

        setHasMoreProjects(Boolean(data.next));
        setProjectPage(pageNum);
      } catch (error) {
        console.error("Error fetching projects:", error);
        toast.error("Failed to load projects");
      } finally {
        setProjectLoading(false);
      }
    },
    [],
  );

  // ========== DIVISION CHANGE HANDLER ==========
  const handleDivisionChange = (event, newValue) => {
    setFormData((prev) => ({
      ...prev,
      division: newValue,
      project: null,
    }));
    setErrors((prev) => ({ ...prev, division: "" }));

    // Reset project options
    setProjectOptions([]);

    // Fetch projects for selected division
    if (newValue?.division_id || newValue?.id) {
      const divisionId = newValue.division_id || newValue.id;
      fetchProjectsForDivision(divisionId, "", 1, true);
    }
  };

  // ========== PROJECT CHANGE HANDLER ==========
  const handleProjectChange = (event, newValue) => {
    setFormData((prev) => ({
      ...prev,
      project: newValue,
    }));
    setErrors((prev) => ({ ...prev, project: "" }));
  };

  // ========== DEBOUNCED SEARCH FOR DIVISIONS ==========
  useEffect(() => {
    const handler = setTimeout(() => {
      if (openAddModal) {
        // fetchDivisions(divisionSearchInput, 1, true);
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [divisionSearchInput, openAddModal]);

  // ========== DEBOUNCED SEARCH FOR PROJECTS ==========
  useEffect(() => {
    const handler = setTimeout(() => {
      if (openAddModal && formData.division) {
        const divisionId = storedDivision?.division_id;
        fetchProjectsForDivision(divisionId, projectSearchInput, 1, true);
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [
    projectSearchInput,
    openAddModal,
    formData.division,
    fetchProjectsForDivision,
  ]);

  // Initial load
  useEffect(() => {
    if (storedDivision?.division_id) {
      fetchData(1, true, "");
    }
  }, []);

  // Debounced search effect for main table
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (!storedDivision?.division_id) {
      setRows([]);
      return;
    }

    const handler = setTimeout(() => {
      setPage(1);
      fetchData(1, true, projectFilter);
    }, 500);

    return () => clearTimeout(handler);
  }, [projectFilter]);

  // GET - Fetch Design & Drawing Data
  const fetchData = async (pageNumber = 1, reset = false, project = "") => {
    try {
      setLoading(true);
      const data = await fetchDesignAndDrawing(pageNumber, {
        project,
        division: storedDivision.division_id,
      });

      const results = data.results || data.data || [];

      if (reset) {
        setRows(results);
      } else {
        setRows((prev) => [...prev, ...results]);
      }

      setHasMore(Boolean(data.next));
      setPage(pageNumber);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

const fetchMoreData = () => {
  if (hasMore && !loading) {
    fetchData(page + 1, false, projectFilter);
  }
}

  // POST - Add/Update Design & Drawing - ULTIMATE FIX
  const handleAddNewRow = async () => {
    const { division, project, file, created_by } = formData;
    const newErrors = {};

    const divisionData = getSelectedDivision();
    if (!divisionData?.division_id) {
      toast.error(
        "No division selected. Please select a division from the header.",
      );
      return;
    }

    if (!division) newErrors.division = "Division is required";
    if (!project) newErrors.project = "Project is required";
    if (!file && !isEditMode) newErrors.file = "File is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setLoading(true);

      // Create FormData only with the fields we actually want to send
      const formDataToSend = new FormData();

      // Handle division - ensure we have the ID
      if (division) {
        const divisionId = division.division_id || division.id || division;
        if (divisionId) {
          formDataToSend.append("division", String(divisionId));
        }
      }

      // Handle project - ensure we have the ID
      if (project) {
        const projectId = project.id || project;
        if (projectId) {
          formDataToSend.append("project", String(projectId));
        }
      }

      formDataToSend.append("created_by", String(created_by));

      // ULTIMATE FIX: Check file more rigorously
      const hasValidFile =
        file &&
        file instanceof File &&
        file.size > 0 &&
        file.name &&
        file.name.trim() !== "";

      if (isEditMode) {
        // In edit mode, only append file if user selected a VALID new one
        if (hasValidFile) {
          formDataToSend.append("file", file);
        }
      } else {
        // In add mode, file is required and must be valid
        if (!hasValidFile) {
          toast.error("Please select a valid file for new record");
          setLoading(false);
          return;
        }
        formDataToSend.append("file", file);
      }

      // Debug: Log what we're sending
      console.log("FormData being sent:");
      for (let pair of formDataToSend.entries()) {
        console.log(`${pair[0]}:`, pair[1]);
      }

      let response;
      if (isEditMode && currentEditId) {
        // Update existing record
        response = await updateDesignAndDrawing(currentEditId, formDataToSend);
        if (response) {
          toast.success(response.message || "Record updated successfully!");
        }
      } else {
        // Add new record
        response = await createDesignAndDrawing(formDataToSend);
        if (response) {
          toast.success(response.message || "Record added successfully!");
        }
      }

      // Reset form and close modal
      setFormData(getInitialFormData());
      setErrors({});
      setIsEditMode(false);
      setCurrentEditId(null);
      setOpenAddModal(false);
      fetchData(1, true, projectFilter);
    } catch (error) {
      console.error("Error saving record:", error);
      console.error("Error details:", error.response?.data);

      // Show specific error messages if available
      if (error.response?.data?.details) {
        const details = error.response.data.details;
        Object.keys(details).forEach((key) => {
          toast.error(`${key}: ${details[key].join(", ")}`);
        });
      } else if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error("Failed to save record.");
      }
    } finally {
      setLoading(false);
    }
  };

  // DELETE
  const handleDelete = async (id) => {
    try {
      const response = await deleteDesignAndDrawing(id);
      if (response) {
        setRows(rows.filter((item) => item.id !== id));
        toast.success("Record deleted successfully!");
      } else {
        toast.error("Failed to delete record.");
      }
    } catch (error) {
      console.error("Error deleting record:", error);
      toast.error("Failed to delete record.");
    }
  };

  // EDIT - Load data for editing
  const handleEdit = async (row) => {
    setIsEditMode(true);
    setCurrentEditId(row.id);

    // First fetch fresh divisions
    // await fetchDivisions("", 1, true);

    // Find division from options or create from row data
    let selectedDivision = null;
    const divisionId = row.division_id || row.division?.id || row.division;

    if (divisionId) {
      // Try to find division from divisionOptions
      selectedDivision = divisionOptions.find(
        (div) => (div.division_id || div.id || div) === divisionId,
      );

      // If not found in current options, create object from row data
      if (!selectedDivision) {
        selectedDivision = {
          id: divisionId,
          division_id: divisionId,
          division:
            row.division_name ||
            row.division?.division ||
            row.division ||
            "Unknown Division",
        };

        // Add to division options so Autocomplete can find it
        setDivisionOptions((prev) => [...prev, selectedDivision]);
      }
    }

    // Find project or create from row data
    let selectedProject = null;
    const projectId = row.project_id || row.project?.id || row.project;

    if (projectId) {
      // First fetch projects for the selected division
      if (
        selectedDivision &&
        (selectedDivision.id || selectedDivision.division_id)
      ) {
        const divisionIdForFetch =
          selectedDivision.id || selectedDivision.division_id;
        await fetchProjectsForDivision(divisionIdForFetch, "", 1, true);
      }

      // Try to find project from projectOptions
      selectedProject = projectOptions.find((proj) => proj.id === projectId);

      // If not found in options, create object from row data
      if (!selectedProject) {
        selectedProject = {
          id: projectId,
          project_name:
            row.project_name ||
            row.project?.project_name ||
            row.project ||
            "Unknown Project",
        };

        // Add to project options so Autocomplete can find it
        setProjectOptions((prev) => [...prev, selectedProject]);
      }
    }

    setFormData({
      division: selectedDivision,
      project: selectedProject,
      file: null, // File will be shown as existing
      created_by: row.created_by || getUserId(),
    });

    setOpenAddModal(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, file: file });
      setErrors({ ...errors, file: "" });
    }
  };

  // Scroll handler for loading more options in Autocomplete
  const handleDivisionScroll = (event) => {
    const listboxNode = event.currentTarget;
    if (
      listboxNode.scrollTop + listboxNode.clientHeight >=
        listboxNode.scrollHeight - 10 &&
      hasMoreDivisions &&
      !divisionLoading
    ) {
      // fetchDivisions(divisionSearchInput, divisionPage + 1, false);
    }
  };

  const handleProjectScroll = (event) => {
    const listboxNode = event.currentTarget;
    if (
      listboxNode.scrollTop + listboxNode.clientHeight >=
        listboxNode.scrollHeight - 10 &&
      hasMoreProjects &&
      !projectLoading
    ) {
      const divisionId =
        formData.division?.division_id || formData.division?.id;
      if (divisionId) {
        fetchProjectsForDivision(
          divisionId,
          projectSearchInput,
          projectPage + 1,
          false,
        );
      }
    }
  };

  // Reset form when closing modal
  const handleCloseModal = () => {
    if (!loading) {
      setFormData(getInitialFormData());
      setErrors({});
      setIsEditMode(false);
      setCurrentEditId(null);
      setProjectOptions([]);
      setProjectSearchInput("");
      setOpenAddModal(false);
    }
  };

  // Mobile Card Component
  const MobileCard = ({ row, index }) => {
    const isExpanded = expandedItem === row.id;

    return (
      <Box key={row.id}>
        <Box
          onClick={() =>
            setExpandedItem(expandedItem === row.id ? null : row.id)
          }
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 1.5,
            cursor: "pointer",
            bgcolor: index % 2 === 0 ? "#ffffff" : "#f5f5f5", // Alternating colors
            "&:active": { bgcolor: "#e3f2fd" },
            borderLeft: "4px solid #4caf50",
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
            >
              <Typography
                sx={{ fontSize: "0.7rem", color: "#666", minWidth: 25 }}
              >
                #{index + 1}
              </Typography>
              <Typography
                sx={{ fontWeight: "bold", fontSize: "0.85rem", flex: 1 }}
              >
                {row.project_name || row.project}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: "#666",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <span style={{ fontSize: "0.7rem" }}>
                {row.division_name || row.division}
              </span>
            </Typography>
          </Box>
          <IconButton size="small" sx={{ ml: 1 }}>
            {isExpanded ? (
              <ExpandLess fontSize="small" />
            ) : (
              <ExpandMore fontSize="small" />
            )}
          </IconButton>
        </Box>

        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              px: 1.5,
              py: 1,
              bgcolor: index % 2 === 0 ? "#ffffff" : "#f5f5f5",
              borderLeft: "4px solid #4caf50",
            }}
          >
            <Box sx={{ mb: 0.8 }}>
              <Typography
                sx={{
                  fontSize: "0.6rem",
                  color: "#999",
                  letterSpacing: "0.5px",
                  mb: 0.3,
                }}
              >
                DIVISION
              </Typography>
              <Typography
                sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}
              >
                {row.division_name || row.division}
              </Typography>
            </Box>

            <Box sx={{ mb: 0.8 }}>
              <Typography
                sx={{
                  fontSize: "0.6rem",
                  color: "#999",
                  letterSpacing: "0.5px",
                  mb: 0.3,
                }}
              >
                PROJECT
              </Typography>
              <Typography
                sx={{ fontSize: "0.7rem", color: "#555", lineHeight: 1.3 }}
              >
                {row.project_name || row.project}
              </Typography>
            </Box>

            <Box sx={{ mb: 0.8 }}>
              <Typography
                sx={{
                  fontSize: "0.6rem",
                  color: "#999",
                  letterSpacing: "0.5px",
                  mb: 0.3,
                }}
              >
                FILE
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 500,
                    color: "#1976d2",
                    cursor: "pointer",
                    flex: 1,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (row.file) {
                      window.open(row.file, "_blank");
                    }
                  }}
                >
                  {getFileName(row.file_name || row.file)}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (row.file) {
                      window.open(row.file, "_blank");
                    }
                  }}
                  sx={{
                    padding: 0.2,
                    color: "#1976d2",
                    "&:hover": { bgcolor: "rgba(25, 118, 210, 0.04)" },
                  }}
                >
                  <VisibilityIcon sx={{ fontSize: "0.9rem" }} />
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ mb: 0.8 }}>
              <Typography
                sx={{
                  fontSize: "0.6rem",
                  color: "#999",
                  letterSpacing: "0.5px",
                  mb: 0.3,
                }}
              >
                CREATED ON
              </Typography>
              <Typography
                sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}
              >
                {formatDate(row.created_at || row.created_date)}
              </Typography>
            </Box>

            <Box sx={{ mb: 0.8 }}>
              <Typography
                sx={{
                  fontSize: "0.6rem",
                  color: "#999",
                  letterSpacing: "0.5px",
                  mb: 0.3,
                }}
              >
                CREATED BY
              </Typography>
              <Typography
                sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}
              >
                {row.created_by_name || row.created_by || "N/A"}
              </Typography>
            </Box>

            <Divider sx={{ my: 1, borderColor: "#e0e0e0" }} />

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.6 }}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(row);
                }}
                sx={{
                  bgcolor: "#e8f5e9",
                  borderRadius: "6px",
                  width: 32,
                  height: 32,
                  "&:hover": { bgcolor: "#c8e6c9", transform: "scale(1.05)" },
                  transition: "all 0.2s",
                }}
              >
                <EditIcon sx={{ fontSize: "1rem", color: "#2e7d32" }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setAnchorEl(e.currentTarget);
                  setItemToDelete(row.id);
                }}
                sx={{
                  bgcolor: "#ffebee",
                  borderRadius: "6px",
                  width: 32,
                  height: 32,
                  "&:hover": { bgcolor: "#ffcdd2", transform: "scale(1.05)" },
                  transition: "all 0.2s",
                }}
              >
                <DeleteIcon sx={{ fontSize: "1rem", color: "#f44336" }} />
              </IconButton>
            </Box>
          </Box>
        </Collapse>

        <Divider />
      </Box>
    );
  };

  return (
    <Box sx={{ mt: 0 }}>
      {/* Search Fields and Add Button */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "stretch" : "center",
          gap: 1,
          mt: -3,
          pt: -2,
          mb: 1,
        }}
      >
        {/* Search Fields Container */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: 1,
            flex: 1,
          }}
        >
          <TextField
            label="Division"
            variant="outlined"
            value={storedDivision?.division || "No Division Selected"}
            disabled
            fullWidth
            sx={{
              "& .MuiInputBase-root": {
                height: 30,
                borderRadius: 0,
                backgroundColor: "#f0f0f0",
              },
              "& .MuiInputLabel-root": { fontSize: "0.7rem", top: -3 },
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: storedDivision?.division_id
                  ? "#333"
                  : "#999",
              },
            }}
          />

          <TextField
            label="Search by Project"
            variant="outlined"
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            fullWidth
            disabled={!storedDivision?.division_id} // ✅ Disable if no division
            sx={{
              "& .MuiInputBase-root": { height: 30, borderRadius: 0 },
              "& .MuiInputLabel-root": { fontSize: "0.7rem", top: -3 },
            }}
          />
        </Box>

        {/* Add Button */}
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setFormData(getInitialFormData());
            setErrors({});
            setProjectOptions([]);
            setProjectSearchInput("");
            setIsEditMode(false);
            setCurrentEditId(null);
            setOpenAddModal(true);
          }}
          disabled={loading}
          sx={{
            height: 30,
            minWidth: 150,
            display: "flex",
            alignItems: "center",
            fontSize: "0.7rem",
            px: 2,
            borderRadius: "0",
            mt: isMobile ? 1 : 0,
          }}
        >
          <AddIcon fontSize="small" sx={{ mr: 0.5 }} /> Design & Drawing
        </Button>
      </Box>

      {/* Data Display */}
      <InfiniteScroll
        dataLength={rows.length}
        next={fetchMoreData}
        hasMore={hasMore}
        height={isMobile ? "70vh" : "80vh"}
        loader={
          <Typography sx={{ textAlign: "center", p: 2 }}>Loading...</Typography>
        }
        endMessage={
          <Typography sx={{ textAlign: "center", p: 2 }}>
            {rows.length === 0 ? "No records found!" : "No more records!"}
          </Typography>
        }
      >
        {isMobile ? (
          <Box sx={{ bgcolor: "background.paper", pb: 2 }}>
            {rows.map((row, index) => (
              <MobileCard key={row.id || index} row={row} index={index} />
            ))}
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{ maxHeight: "80vh", overflow: "auto" }}
          >
            <Table
              stickyHeader
              size="small"
              sx={{
                minWidth: 900,
                "& td, & th": {
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  backgroundColor: "#f5f5f5",
                  borderBottom: "1px solid #ccc",
                },
              }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "#f5f5f5",
                    "& th": {
                      fontWeight: "bold",
                      color: "#333",
                      borderBottom: "1px solid #999",
                      whiteSpace: "nowrap",
                      backgroundColor: "#f5f5f5",
                      fontSize: "0.80rem",
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      backgroundColor: "#f5f5f5",
                      zIndex: 3,
                    }}
                  >
                    S. No.
                  </TableCell>
                  <TableCell>Division</TableCell>
                  <TableCell>Project</TableCell>
                  <TableCell>File</TableCell>
                  <TableCell>Created Date</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell
                    align="center"
                    className="no-print"
                    sx={{
                      position: "sticky",
                      right: 0,
                      backgroundColor: "#f5f5f5",
                      zIndex: 3,
                    }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((row, index) => (
                  <TableRow
                    hover
                    key={row.id}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff", // 🔹 Alternate row color
                      "& td": {
                        padding: "0px",
                        fontSize: "0.7rem",
                        fontSize: { xs: "0.75rem", sm: "0.8125rem" }, // responsive font
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: 120,
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark" ? "black" : "inherit",
                        color: (theme) =>
                          theme.palette.mode === "dark" ? "white" : "inherit",
                      },
                      "&:hover": {
                        backgroundColor: "#e3f2fd !important", // hover color
                      },
                    }}
                  >
                    <TableCell sx={{ position: "sticky", left: 0, zIndex: 0 }}>
                      {index + 1}
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title={row.division_name || row.division || ""}
                        arrow
                      >
                        <span>
                          {truncateText(row.division_name || row.division, 20)}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title={row.project_name || row.project || ""}
                        arrow
                      >
                        <span>
                          {truncateText(row.project_name || row.project, 20)}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.5,
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (row.file) window.open(row.file, "_blank");
                          }}
                          sx={{
                            padding: 0.2,
                            color: "#1976d2",
                            "&:hover": { bgcolor: "rgba(25, 118, 210, 0.04)" },
                          }}
                        >
                          <VisibilityIcon sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {formatDate(row.created_at || row.created_date)}
                    </TableCell>
                    <TableCell>
                      {row.created_by_name || row.created_by || "N/A"}
                    </TableCell>
                    <TableCell
                      align="center"
                      className="no-print"
                      sx={{
                        position: "sticky",
                        right: 0,
                        zIndex: 0,
                        minWidth: 120,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          gap: 0.5,
                        }}
                      >
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEdit(row)}
                          sx={{
                            bgcolor: "#e8f5e9",
                            "&:hover": { bgcolor: "#c8e6c9" },
                          }}
                        >
                          <EditIcon sx={{ fontSize: "0.9rem" }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            setAnchorEl(e.currentTarget);
                            setItemToDelete(row.id);
                          }}
                          sx={{
                            bgcolor: "#ffebee",
                            "&:hover": { bgcolor: "#ffcdd2" },
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: "0.9rem" }} />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </InfiniteScroll>

      {/* Add/Edit Modal with Cascading Dropdowns */}
      <Dialog
        open={openAddModal}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isEditMode ? "Edit Design & Drawing" : "Add New Design & Drawing"}
        </DialogTitle>
        <DialogContent>
          {/* DIVISION AUTOCOMPLETE - First Step */}
          <TextField
            fullWidth
            label="Division"
            size="small"
            value={storedDivision?.division || "No Division Selected"}
            disabled
            InputProps={{
              sx: {
                backgroundColor: "#f5f5f5",
                height: 40,
              },
            }}
            helperText="Division is set globally"
            error={!storedDivision?.division_id}
          />

          {/* PROJECT AUTOCOMPLETE - Loaded based on selected division */}
          <Autocomplete
            value={formData.project}
            onChange={handleProjectChange}
            onInputChange={(event, newInputValue) => {
              setProjectSearchInput(newInputValue);
            }}
            options={projectOptions}
            getOptionLabel={(option) => option?.project_name || ""}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            loading={projectLoading}
            disabled={!formData.division || loading}
            ListboxProps={{
              onScroll: handleProjectScroll,
              style: { maxHeight: 200 },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Project *"
                margin="dense"
                fullWidth
                error={Boolean(errors.project)}
                helperText={
                  errors.project ||
                  (!formData.division
                    ? "Select division first"
                    : "Select a project")
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {projectLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {/* File Upload */}
          <Box sx={{ mt: 2 }}>
            <Typography
              variant="body2"
              sx={{
                mb: 1,
                color: errors.file ? "error.main" : "text.secondary",
              }}
            >
              Upload File {!isEditMode ? "*" : ""}{" "}
              {isEditMode && "(Optional - leave empty to keep existing file)"}
            </Typography>
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUploadIcon />}
              fullWidth
              disabled={loading}
              sx={{
                height: 56,
                borderColor: errors.file ? "error.main" : "divider",
                borderStyle: "dashed",
                "&:hover": {
                  borderColor: errors.file ? "error.main" : "primary.main",
                  borderStyle: "dashed",
                },
              }}
            >
              {formData.file
                ? formData.file.name
                : isEditMode
                  ? "Choose new file (optional)"
                  : "Choose File *"}
              <input
                type="file"
                hidden
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx,.dwg,.dxf,.png,.jpg,.jpeg"
              />
            </Button>
            {errors.file && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 0.5, display: "block" }}
              >
                {errors.file}
              </Typography>
            )}
            {isEditMode && !formData.file && (
              <Box sx={{ mt: 1, p: 1, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Current file:{" "}
                  {rows.find((row) => row.id === currentEditId)?.file_name ||
                    getFileName(
                      rows.find((row) => row.id === currentEditId)?.file,
                    )}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="error" disabled={loading}>
            Close
          </Button>
          <Button
            onClick={handleAddNewRow}
            color="primary"
            variant="contained"
            disabled={loading}
          >
            {loading
              ? isEditMode
                ? "Updating..."
                : "Adding..."
              : isEditMode
                ? "Update"
                : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="body2">
            Are you sure you want to delete?
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mt: 1,
              gap: 1,
            }}
          >
            <Button
              color="error"
              size="small"
              variant="contained"
              onClick={() => {
                handleDelete(itemToDelete);
                setAnchorEl(null);
                setItemToDelete(null);
              }}
            >
              Yes
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setAnchorEl(null);
                setItemToDelete(null);
              }}
            >
              No
            </Button>
          </Box>
        </Box>
      </Popover>

      <ToastContainer position="top-right" autoClose={3000} />
    </Box>
  );
};

export default DesignAndDrawing;
